import {Button, Card, CircularProgress, Divider} from "@mui/material";
import {OpenInNew} from "@mui/icons-material";
import userService from "../../../../services/userService";
import {IUserModel} from "../../../../types/UserModel";
import userVerificationService from "../../../../services/userVerificationService";
import {IUserVerification} from "../../../../types/UserVerification";
import {FunctionComponent, useState} from "react";
import bgcService from "../../../../services/bgcService";
import {IBackgroundCheck} from "../../../../types/IBackgroundCheck";
import {SkipAdminStatus} from "../../../../constants/SkipAdminStatus";
import {Timestamp} from "firebase/firestore";
import BgcReviewedModal from "./BgcReviewedModal";

export const UserActions: FunctionComponent<{
    userId?: string;
    user?: IUserModel;
    setUser: (user: IUserModel) => void;
    userVerification?: IUserVerification;
    setUserVerification: (userVerification: IUserVerification) => void;
    backgroundCheck?: IBackgroundCheck;
    setBackgroundCheck: (bgc: IBackgroundCheck) => void;
    checkrCandidateId?: string | null;
    onBan: () => void;
    onRequestChanges: () => void;
}> = ({userId, user, setUser, userVerification, setUserVerification, backgroundCheck, setBackgroundCheck, checkrCandidateId, onBan, onRequestChanges}) => {
    const [loading, setLoading] = useState<string | null>(null);
    const [isBgcReviewedModalOpen, setIsBgcReviewedModalOpen] = useState(false);

    const bgcStatus = backgroundCheck?.skipAdminStatus ?? null;

    async function setBgcStatus(status: SkipAdminStatus) {
        setLoading('bgc');
        await bgcService.setSkipAdminStatus(userId!, status);
        setBackgroundCheck({...(backgroundCheck ?? {id: userId!}), skipAdminStatus: status});
        setLoading(null);
    }

    async function markBgcReviewed(adverseActionDate: Date) {
        setLoading('bgc');
        await bgcService.setSkipAdminStatus(userId!, SkipAdminStatus.REVIEWED, adverseActionDate);
        setBackgroundCheck({
            ...(backgroundCheck ?? {id: userId!}),
            skipAdminStatus: SkipAdminStatus.REVIEWED,
            adverseActionDate: Timestamp.fromDate(adverseActionDate),
        });
        setLoading(null);
    }

    return (
        <Card sx={{display: 'flex', flexDirection: 'column', alignItems: 'start'}}>
            <Button disabled={loading === 'reviewed'} onClick={async () => {
                setLoading('reviewed');
                const updates = await userService.toggleUserReviewed(userId!, !user?.isReviewed);
                setUser({...user, ...updates} as IUserModel);
                setLoading(null);
            }} endIcon={loading === 'reviewed' && <CircularProgress size={16}/>}>
                {user?.isReviewed ? 'Reviewed' : 'Mark as reviewed'}
            </Button>

            <Button disabled={loading === 'approved'} onClick={async () => {
                setLoading('approved');
                const updates = await userService.toggleUserApproval(userId!, !user?.isApproved, !!user?.changesRequested);
                setUser({...user, ...updates} as IUserModel);
                setLoading(null);
            }} endIcon={loading === 'approved' && <CircularProgress size={16}/>}>
                {user?.isApproved ? 'Approved' : 'Mark as approved'}
            </Button>

            <Button
                disabled={loading === 'bgc' || bgcStatus !== null}
                onClick={() => setIsBgcReviewedModalOpen(true)}
                endIcon={loading === 'bgc' && <CircularProgress size={16}/>}
            >
                {bgcStatus !== null ? 'BGC Reviewed' : 'Mark BGC Reviewed'}
            </Button>

            <BgcReviewedModal
                isOpen={isBgcReviewedModalOpen}
                onClose={() => setIsBgcReviewedModalOpen(false)}
                onConfirm={markBgcReviewed}
            />

            <Button onClick={() => onRequestChanges()}>
                {user?.changesRequested ? 'Changes requested' : 'Request Changes'}
            </Button>

            <Button color={'success'} disabled={loading === 'profileVerified'} onClick={async () => {
                setLoading('profileVerified');
                const reviewUpdate = await userService.toggleUserReviewed(userId!, true);
                setUser({...user, ...reviewUpdate} as IUserModel);
                const updates = await userVerificationService.toggleUserVerification(userId!, !userVerification?.isVerified);
                setUserVerification({...userVerification, ...updates} as IUserVerification);
                setLoading(null);
            }} endIcon={loading === 'profileVerified' && <CircularProgress size={16}/>}>
                {userVerification?.isVerified ? 'Profile Verified' : 'Verify Profile'}
            </Button>

            <Button color={'success'} disabled={loading === 'idVerified'} onClick={async () => {
                setLoading('idVerified');
                const verifying = !userVerification?.idVerified;
                const updates = await userVerificationService.toggleIdVerification(userId!, verifying);
                setUserVerification({...userVerification, ...updates} as IUserVerification);
                
                setLoading(null);
            }} endIcon={loading === 'idVerified' && <CircularProgress size={16}/>}>
                {userVerification?.idVerified ? 'ID Verified' : 'Verify ID'}
            </Button>

            <Button
                color={'success'}
                disabled={loading === 'bgc' || bgcStatus === SkipAdminStatus.APPROVED || bgcStatus === SkipAdminStatus.DENIED}
                onClick={() => setBgcStatus(SkipAdminStatus.APPROVED)}
                endIcon={loading === 'bgc' && <CircularProgress size={16}/>}
            >
                {bgcStatus === SkipAdminStatus.APPROVED ? 'Approved BGC' : 'Approve BGC'}
            </Button>

            <Button
                color={'error'}
                disabled={loading === 'bgc' || bgcStatus === SkipAdminStatus.DENIED || bgcStatus === SkipAdminStatus.APPROVED}
                onClick={() => setBgcStatus(SkipAdminStatus.DENIED)}
                endIcon={loading === 'bgc' && <CircularProgress size={16}/>}
            >
                {bgcStatus === SkipAdminStatus.DENIED ? 'Denied BGC Final' : 'Deny BGC Final'}
            </Button>

            <Button color={'error'} onClick={() => onBan()}>Ban User</Button>

            <Divider sx={{width: '100%'}}/>

            <Button
                endIcon={<OpenInNew/>}
                disabled={!checkrCandidateId}
                onClick={() => {
                    console.log(process.env);
                    const base = process.env.REACT_APP_FIREBASE_PROJECT_ID === 'limit-f7d43'
                        ? 'https://dashboard.checkrhq-staging.net'
                        : 'https://dashboard.checkr.com';
                    window.open(`${base}/candidates/${checkrCandidateId}/`, '_blank');
                }}
            >
                Checkr Link
            </Button>
        </Card>
    );
};
